/* Publieke aanbod-view: ouder bekijkt aanbod via secure token en accepteert/wijst af.
   Route: /aanbod/:token */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Check, X, Sparkles, Clock, Baby, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: false,
});

const DAG_LABELS = ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"];

export default function AanbodPage() {
  const { token } = useParams();
  const [aanbod, setAanbod] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAccept, setShowAccept] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [naam, setNaam] = useState("");
  const [confirmAV, setConfirmAV] = useState(false);
  const [opmerking, setOpmerking] = useState("");
  const [reactie, setReactie] = useState(null);

  const load = () => {
    api.get(`/public/aanbod/${token}`)
      .then(r => setAanbod(r.data))
      .catch(e => setErr(formatApiError(e.response?.data?.detail) || "Aanbod niet gevonden"));
  };
  useEffect(() => { load(); }, [token]);

  const primair = aanbod?.kleur_primair || "#2D4A3E";
  const isVerlopen = aanbod?.status === "aanbod_verlopen";
  const isBeslist = aanbod && ["aanbod_geaccepteerd", "aanbod_afgewezen"].includes(aanbod.status);
  const kanReageren = aanbod?.status === "aanbod_verstuurd";

  const submit = async (accept) => {
    if (accept && (!naam.trim() || !confirmAV)) {
      toast.error(!naam.trim() ? "Vul uw volledige naam in" : "Bevestig de voorwaarden");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/public/aanbod/${token}/decide`, {
        accept,
        ondertekenaar_naam: naam.trim() || null,
        accept_voorwaarden: !!confirmAV,
        opmerking: opmerking.trim() || null,
      });
      setReactie(data);
      setShowAccept(false); setShowReject(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reactie mislukt");
    } finally { setBusy(false); }
  };

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-neutral-50">
        <Card className="max-w-md p-8 text-center bg-white">
          <div className="text-xl font-display mb-2">Aanbod niet gevonden</div>
          <div className="text-sm text-neutral-600">{err}</div>
        </Card>
      </div>
    );
  }
  if (!aanbod) {
    return <div className="min-h-screen flex items-center justify-center text-neutral-500">Laden…</div>;
  }

  return (
    <div className="min-h-screen p-6" style={{ background: `linear-gradient(135deg, ${primair}12, ${primair}05)` }} data-testid="aanbod-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          {aanbod.organisation_logo ? (
            <img src={aanbod.organisation_logo.startsWith("http") ? aanbod.organisation_logo : `${process.env.REACT_APP_BACKEND_URL}${aanbod.organisation_logo}`}
                 alt="logo" className="w-14 h-14 rounded-xl object-cover" />
          ) : (
            <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: primair }}>
              <Baby className="h-7 w-7 text-white" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-display leading-tight">{aanbod.organisation_name}</h1>
            <div className="text-xs text-neutral-500">Plaatsingsaanbod</div>
          </div>
        </div>

        {/* Aanbod card */}
        <Card className="p-6 bg-white mb-4">
          <div className="flex items-start justify-between flex-wrap gap-2 mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Aanbod voor</div>
              <div className="text-xl font-display" data-testid="aanbod-kind-naam">
                {aanbod.kind_voornaam} {aanbod.kind_achternaam}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Status</div>
              <div className="text-sm font-medium" data-testid="aanbod-status">
                {isVerlopen ? "Verlopen" : isBeslist ? (aanbod.status === "aanbod_geaccepteerd" ? "Geaccepteerd" : "Afgewezen") : "Wacht op reactie"}
              </div>
            </div>
          </div>

          {aanbod.aanbod_boodschap && (
            <div className="p-3 mb-4 rounded-md text-sm" style={{ background: `${primair}0d`, borderLeft: `3px solid ${primair}` }}>
              {aanbod.aanbod_boodschap}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Locatie</div>
              <div className="font-medium">{aanbod.location_name} · {aanbod.location_type}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Groep</div>
              <div className="font-medium">{aanbod.group_name}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Startdatum</div>
              <div className="font-medium">{aanbod.aanbod_startdatum}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Uren/week</div>
              <div className="font-medium">{aanbod.aanbod_uren_per_week} · € {aanbod.aanbod_uurtarief?.toFixed?.(2)}/u</div>
            </div>
          </div>

          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Opvang-dagen</div>
            <div className="flex flex-wrap gap-1">
              {(aanbod.aanbod_dagen || []).map((d, i) => (
                <span key={i} className="text-xs px-2 py-1 rounded" style={{ background: `${primair}12`, color: primair }}>
                  {DAG_LABELS[d.weekday]} · {d.dagdeel}
                </span>
              ))}
            </div>
          </div>

          {aanbod.aanbod_valid_until && !isBeslist && !isVerlopen && (
            <div className="mt-4 text-xs text-neutral-500 flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> Geldig tot {String(aanbod.aanbod_valid_until).slice(0, 10)}
            </div>
          )}
        </Card>

        {/* Reageren */}
        {kanReageren && !showAccept && !showReject && (
          <div className="flex gap-3">
            <Button onClick={() => setShowAccept(true)} data-testid="btn-accepteer"
              style={{ background: primair }} className="text-white flex-1 h-12">
              <Check className="h-5 w-5 mr-2" /> Aanbod accepteren
            </Button>
            <Button onClick={() => setShowReject(true)} data-testid="btn-afwijs"
              variant="outline" className="flex-1 h-12">
              <X className="h-5 w-5 mr-2" /> Afwijzen
            </Button>
          </div>
        )}

        {/* Accept-formulier */}
        {showAccept && (
          <Card className="p-6 bg-white" data-testid="accept-form">
            <h3 className="font-display text-lg mb-1">Aanbod accepteren</h3>
            <p className="text-xs text-neutral-500 mb-4">Bevestig uw acceptatie met uw volledige naam. Wij stellen daarna het contract op.</p>
            <div className="space-y-3">
              <div>
                <Label>Uw volledige naam (digitale ondertekening) *</Label>
                <Input value={naam} onChange={e => setNaam(e.target.value)} placeholder="Voor- en achternaam" data-testid="ondertekenaar-naam" />
              </div>
              <div>
                <Label>Opmerking (optioneel)</Label>
                <Textarea rows={3} value={opmerking} onChange={e => setOpmerking(e.target.value)} />
              </div>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox checked={confirmAV} onCheckedChange={v => setConfirmAV(!!v)} data-testid="accept-av-checkbox" />
                <span>Ik ga akkoord met de plaatsingsvoorwaarden en het uurtarief. Ik verwacht binnenkort een contract ter ondertekening.</span>
              </label>
              <div className="text-xs text-neutral-500 flex items-start gap-2 mt-2">
                <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" style={{ color: primair }} />
                <span>Uw acceptatie wordt met tijdstempel en IP-adres vastgelegd — juridisch geldig.</span>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={() => setShowAccept(false)}>Terug</Button>
              <Button onClick={() => submit(true)} disabled={busy || !naam.trim() || !confirmAV}
                style={{ background: primair }} className="text-white" data-testid="confirm-accepteer">
                {busy ? "Bezig…" : <><Sparkles className="h-4 w-4 mr-1" /> Bevestig acceptatie</>}
              </Button>
            </div>
          </Card>
        )}

        {/* Reject-formulier */}
        {showReject && (
          <Card className="p-6 bg-white" data-testid="reject-form">
            <h3 className="font-display text-lg mb-1">Aanbod afwijzen</h3>
            <p className="text-xs text-neutral-500 mb-4">Waarom wijst u dit aanbod af? (optioneel)</p>
            <Textarea rows={3} value={opmerking} onChange={e => setOpmerking(e.target.value)}
              placeholder="Bijv. andere opvang gevonden, timing niet passend, etc." data-testid="afwijs-opmerking" />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={() => setShowReject(false)}>Terug</Button>
              <Button onClick={() => submit(false)} disabled={busy} variant="outline" data-testid="confirm-afwijs">
                {busy ? "Bezig…" : "Bevestig afwijzing"}
              </Button>
            </div>
          </Card>
        )}

        {/* Reactie ontvangen */}
        {(isBeslist || reactie) && (
          <Card className="p-6 bg-white text-center" data-testid="reactie-ontvangen">
            <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-3"
                 style={{ background: aanbod.status === "aanbod_afgewezen" ? "#dc2626" : primair }}>
              {aanbod.status === "aanbod_afgewezen" ? <X className="h-7 w-7 text-white" /> : <Check className="h-7 w-7 text-white" />}
            </div>
            <div className="font-display text-lg mb-1">
              {aanbod.status === "aanbod_afgewezen" ? "Aanbod afgewezen" : "Aanbod geaccepteerd"}
            </div>
            <div className="text-sm text-neutral-600 max-w-md mx-auto">
              {aanbod.status === "aanbod_afgewezen"
                ? "Uw afwijzing is genoteerd. Neem gerust contact op als u vragen heeft."
                : "Bedankt! Wij stellen nu het contract op. U ontvangt binnenkort een e-mail met de link om digitaal te ondertekenen."}
            </div>
          </Card>
        )}

        {isVerlopen && (
          <Card className="p-6 bg-white text-center border-red-200">
            <div className="font-display text-lg mb-1">Aanbod verlopen</div>
            <div className="text-sm text-neutral-600">De geldigheidsduur is verstreken. Neem contact op met {aanbod.organisation_name}.</div>
          </Card>
        )}

        <div className="text-center text-[10px] text-neutral-400 mt-6">
          {aanbod.organisation_name} · Powered by <strong>Webster</strong>
        </div>
      </div>
    </div>
  );
}
